const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* =====================================================================
 * COMMERCE RESILIENCE — Tier 0 / Tier 2. Added 11 Sep.
 *
 * WHY IT LIVES HERE: this must run on every page, and as early as possible so
 * its capture-phase listeners register before app scripts. The alternative was
 * editing layout/theme.liquid (23KB, Tier 0) to render a snippet. Rewriting
 * that whole file by hand to insert one line carried more risk to revenue than
 * appending to this 258-byte file, which already loads first with `defer` on
 * every page. If this grows, move it to its own asset and add a <script> tag
 * as the FIRST script in theme.liquid. Tracked in the engineering report.
 *
 * CONTEXT THAT MATTERS: on this storefront an app (Opus cart drawer) owns the
 * add-to-cart click. It intercepts the button before the theme's <product-form>
 * submit handler ever runs — verified 11 Sep: no submit event fires at all.
 * That is why these guards live at document capture level rather than in
 * product-form.js, which is effectively dead code for ATC while that app is
 * installed.
 *
 *  1. CHECKOUT ESCAPE HATCH (Tier 0). The primary checkout CTA is rendered by
 *     the Opus drawer inside a shadow root: a <button> with no href and no
 *     form, entirely dependent on that app's JavaScript. If that script is
 *     slow, blocked or throws, the customer sees a checkout button that does
 *     nothing and has no visible way forward. This watches for a checkout
 *     click that produces no navigation and then reveals a native /checkout
 *     link. It deliberately does NOT auto-navigate: the app may be mid-flight
 *     building a checkout URL carrying a discount, and pre-empting could
 *     silently drop it. Showing the customer a door is safer than pushing
 *     them through a possibly wrong one.
 *
 *  2. ADD-TO-CART DOUBLE-TAP GUARD (Tier 0). Verified 11 Sep: three rapid taps
 *     on Add to Cart produced quantity 3. The app's handler has no in-flight
 *     lock. This suppresses a repeat click on the SAME control inside 1s.
 *     Best-effort: it depends on this listener registering before the app's.
 *     The durable fix is in the app's own configuration.
 *
 *  3. THIRD-PARTY ERROR VISIBILITY (Tier 2). Classifies uncaught errors by
 *     originating script so an AOV/pixel/Clarity crash is never mistaken for a
 *     commerce failure. Never calls preventDefault: observed, never swallowed.
 *
 *  4. DIAGNOSTICS (Tier 2). window.CommerceDiag.report() tags the Microsoft
 *     Clarity session already installed on every page. No new vendor, no new
 *     endpoint. A tagged session is replayable in Clarity to watch the exact
 *     customer who failed. Ring buffer at window.CommerceDiag.log.
 *
 * Wrapped so it can never itself break commerce.
 * ===================================================================== */
(function () {
  'use strict';

  try {
    var RING = [];
    var RING_MAX = 50;

    function clarityTag(key, value) {
      try {
        if (typeof window.clarity === 'function') window.clarity('set', key, String(value));
      } catch (_) {}
    }

    window.CommerceDiag = {
      log: RING,
      report: function (stage, detail) {
        try {
          var entry = { t: Date.now(), stage: stage, detail: detail || {} };
          RING.push(entry);
          if (RING.length > RING_MAX) RING.shift();

          if (
            stage === 'atc_failed' ||
            stage === 'atc_rejected' ||
            stage === 'checkout_stalled' ||
            stage === 'atc_double_tap_suppressed'
          ) {
            clarityTag('commerce_error', stage);
            if (detail && detail.reason) clarityTag('commerce_error_reason', detail.reason);
            if (detail && detail.variantId) clarityTag('commerce_error_variant', detail.variantId);
          }
        } catch (_) {}
      },
    };

    var THEME_HOST = window.location.host;

    function classify(src) {
      if (!src) return 'inline';
      try {
        var host = new URL(src, window.location.href).host;
        if (host === THEME_HOST && src.indexOf('/cdn/shop/t/') !== -1) return 'theme';
        if (host === THEME_HOST) return 'shopify';
        if (host.indexOf('cdn.shopify.com') !== -1) return 'shopify-app';
        return 'third-party:' + host;
      } catch (_) {
        return 'unknown';
      }
    }

    window.addEventListener(
      'error',
      function (e) {
        if (!e || !e.filename) return;
        var origin = classify(e.filename);
        window.CommerceDiag.report('js_error', { origin: origin, message: e.message });
        if (origin.indexOf('third-party') === 0) clarityTag('third_party_error', origin);
      },
      true
    );

    window.addEventListener('unhandledrejection', function (e) {
      var reason = '';
      try {
        reason = String((e && e.reason && e.reason.message) || (e && e.reason) || '');
      } catch (_) {}
      window.CommerceDiag.report('unhandled_rejection', { reason: reason.slice(0, 200) });
    });

    /* ---------- 2. Add-to-cart double-tap guard ----------------------- */
    var lastAtc = { el: null, t: 0 };
    var ATC_WINDOW_MS = 1000;

    function looksLikeAddToCart(el) {
      if (!el || !el.tagName) return false;
      var tag = el.tagName.toLowerCase();
      if (tag !== 'button' && tag !== 'input') return false;
      if (el.getAttribute && el.getAttribute('name') === 'add') return true;
      var cls = (el.className && el.className.baseVal) || el.className || '';
      if (typeof cls === 'string' && /product-form__submit|quick-add__submit/.test(cls)) return true;
      return false;
    }

    document.addEventListener(
      'click',
      function (e) {
        try {
          var path = (e.composedPath && e.composedPath()) || [];
          var target = null;
          for (var i = 0; i < path.length && i < 8; i++) {
            if (looksLikeAddToCart(path[i])) {
              target = path[i];
              break;
            }
          }
          if (!target) return;

          var now = Date.now();
          if (lastAtc.el === target && now - lastAtc.t < ATC_WINDOW_MS) {
            e.preventDefault();
            e.stopImmediatePropagation();
            window.CommerceDiag.report('atc_double_tap_suppressed', { dt: now - lastAtc.t });
            return;
          }
          lastAtc.el = target;
          lastAtc.t = now;
        } catch (_) {}
      },
      true
    );

    /* ---------- 1. Checkout escape hatch ------------------------------ */
    var GRACE_MS = 4000;
    var leaving = false;
    var pending = null;
    var banner = null;

    function buildBanner() {
      if (banner || !document.body) return banner;

      var style = document.createElement('style');
      style.textContent =
        '.commerce-fallback{position:fixed;left:0;right:0;bottom:0;z-index:2147483000;display:none;' +
        'padding:12px 16px calc(12px + env(safe-area-inset-bottom,0px));background:#111a1d;color:#fff;' +
        'font-size:14px;line-height:1.4;text-align:center}' +
        '.commerce-fallback[data-visible="true"]{display:block}' +
        '.commerce-fallback__link{display:inline-block;margin-top:8px;padding:11px 22px;min-height:44px;' +
        'background:#fff;color:#111a1d;font-weight:700;text-decoration:none;border-radius:8px}' +
        '.commerce-fallback__link:focus-visible{outline:3px solid #5fc3bd;outline-offset:2px}';
      document.head.appendChild(style);

      banner = document.createElement('div');
      banner.className = 'commerce-fallback';
      banner.id = 'CommerceFallback';
      banner.setAttribute('role', 'status');
      banner.setAttribute('aria-live', 'polite');
      banner.setAttribute('data-visible', 'false');

      var msg = document.createElement('span');
      msg.textContent = 'Checkout is taking longer than usual.';

      var link = document.createElement('a');
      link.className = 'commerce-fallback__link';
      link.href = '/checkout';
      link.id = 'CommerceFallbackLink';
      link.textContent = 'Continue to checkout →';

      banner.appendChild(msg);
      banner.appendChild(document.createElement('br'));
      banner.appendChild(link);
      document.body.appendChild(banner);
      return banner;
    }

    ['pagehide', 'beforeunload'].forEach(function (evt) {
      window.addEventListener(evt, function () {
        leaving = true;
        clearPending();
      });
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        leaving = true;
        clearPending();
      }
    });

    function clearPending() {
      if (pending) {
        clearTimeout(pending);
        pending = null;
      }
    }

    function showBanner() {
      if (leaving) return;
      var b = buildBanner();
      if (!b) return;
      b.setAttribute('data-visible', 'true');
      window.CommerceDiag.report('checkout_stalled', { path: window.location.pathname });
    }

    function looksLikeCheckout(el) {
      if (!el || !el.tagName) return false;
      var tag = el.tagName.toLowerCase();
      if (tag !== 'button' && tag !== 'a' && tag !== 'input') return false;

      if (el.getAttribute && el.getAttribute('name') === 'checkout') return true;

      var href = el.getAttribute && el.getAttribute('href');
      if (href && href.indexOf('/checkout') !== -1) return true;

      var cls = (el.className && el.className.baseVal) || el.className || '';
      if (typeof cls === 'string' && cls.toLowerCase().indexOf('checkout') !== -1) return true;

      var text = (el.textContent || '').trim();
      if (text.length < 60 && /checkout/i.test(text)) return true;

      return false;
    }

    document.addEventListener(
      'click',
      function (e) {
        try {
          if (e.target && e.target.id === 'CommerceFallbackLink') return;

          var path = (e.composedPath && e.composedPath()) || [];
          var hit = false;
          for (var i = 0; i < path.length && i < 12; i++) {
            if (looksLikeCheckout(path[i])) {
              hit = true;
              break;
            }
          }
          if (!hit) return;

          var startedAt = window.location.href;
          clearPending();
          pending = setTimeout(function () {
            if (leaving) return;
            if (window.location.href !== startedAt) return;
            showBanner();
          }, GRACE_MS);
        } catch (_) {}
      },
      true
    );
  } catch (_) {}
})();
